import FormBuilderOb from '@components/FormBuilder/FormBuilder.ob';
import { FormIds } from '@utils/enums/FormIds.enum';
import { usePatientStore } from '@store/patientStore';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NewPatientSection } from '@components/NewPatientSection/NewPatientSection';
import { MultiplePatientsNavigator } from '@components/MultiplePatientsNavigator/MultiplePatientsNavigator';
import { Helmet } from 'react-helmet';
import { t } from 'i18next';
import useContinueValidationFeedback from '@hooks/useContinueValidationFeedback';
import { StepsStep } from '@utils/enums/StepsStep.enum';
import {
  buildInitialCustomPageEnableList,
  getIncompleteCustomPagePatientNames,
  isCustomPageStepComplete,
  updateCustomPageEnableList,
} from './CustomPage.utils';

interface InsuranceProps {
  setIsStepValid: (val: boolean) => void;
}

const CUSTOM_PAGE_FORMS = [
  { key: 'customPagePrimaryPatient', patientIndex: 0 },
  { key: 'customPageSecondaryPatient', patientIndex: 1 },
  { key: 'customPageTertiaryPatient', patientIndex: 2 },
] as const;

const CustomPage = ({ setIsStepValid }: InsuranceProps) => {
  const updatePatient = usePatientStore(state => state.updatePatient);
  const showAddNewPatientFrame = useOnlineBookingStore(
    state => state.showAddPatientFrame,
  );
  const currPatientIndex = useOnlineBookingStore(state => state.currentPatient);
  const patients = usePatientStore(state => state.patients);

  const [listEnableButton, setListEnableButton] = useState<
    Array<boolean | undefined>
  >([]);

  const isCustomPageStepValid = useMemo(
    () => isCustomPageStepComplete(listEnableButton),
    [listEnableButton],
  );

  const getIncompletePatientNames = useCallback(
    () =>
      getIncompleteCustomPagePatientNames({
        currentPatientIndex: currPatientIndex,
        fallbackPatientName: t('detailsSection.newPatient'),
        listEnableButton,
        patients,
      }),
    [currPatientIndex, listEnableButton, patients],
  );

  const handleEnableButton = useCallback((index: number, enable: boolean) => {
    setListEnableButton(prev =>
      updateCustomPageEnableList({
        currentList: prev,
        enable,
        index,
      }),
    );
  }, []);

  const {
    submitAttemptCount: newPatientSubmitAttemptCount,
    resetSubmitAttemptCount: resetNewPatientSubmitAttemptCount,
  } = useContinueValidationFeedback({
    step: StepsStep.CUSTOM_PAGE,
    enabled: showAddNewPatientFrame,
    resetWhen: !showAddNewPatientFrame,
  });

  const { submitAttemptCount: customPageSubmitAttemptCount } =
    useContinueValidationFeedback({
      step: StepsStep.CUSTOM_PAGE,
      enabled: !showAddNewPatientFrame,
      resetWhen: !showAddNewPatientFrame && isCustomPageStepValid,
      getIncompletePatientNames,
    });

  const handleNewPatientEnableButton = useCallback(
    (enable: boolean) => {
      setIsStepValid(enable);

      if (enable) {
        resetNewPatientSubmitAttemptCount();
      }
    },
    [resetNewPatientSubmitAttemptCount, setIsStepValid],
  );

  useEffect(() => {
    if (showAddNewPatientFrame) {
      setIsStepValid(false);
      return;
    }

    setIsStepValid(isCustomPageStepValid);
  }, [isCustomPageStepValid, setIsStepValid, showAddNewPatientFrame]);

  useEffect(() => {
    setListEnableButton(buildInitialCustomPageEnableList(patients));
  }, [patients]);

  return (
    <>
      <Helmet>
        <title>{t('wcag.ob_custom_page_title')}</title>
      </Helmet>
      {showAddNewPatientFrame ? (
        <NewPatientSection
          setEnableButton={handleNewPatientEnableButton}
          submitAttemptCount={newPatientSubmitAttemptCount}
        />
      ) : (
        patients && (
          <>
            {CUSTOM_PAGE_FORMS.filter(
              ({ patientIndex }) => patientIndex < patients.length,
            ).map(({ key, patientIndex }) => {
              return (
                <FormBuilderOb
                  key={key}
                  formIds={[FormIds.CustomPage]}
                  isVisible={currPatientIndex === patientIndex}
                  setEnableButton={enable =>
                    handleEnableButton(patientIndex, enable)
                  }
                  submitAttemptCount={
                    currPatientIndex === patientIndex
                      ? customPageSubmitAttemptCount
                      : 0
                  }
                  onUnmount={formData => {
                    updatePatient(patientIndex, FormIds.CustomPage, formData);
                  }}
                  patientIndex={patientIndex}
                  mykey={`customPage_${patientIndex}`}
                />
              );
            })}
            <MultiplePatientsNavigator
              style={{ padding: '0rem 0rem 1.5rem 0rem' }}
            />
          </>
        )
      )}
    </>
  );
};

export default CustomPage;
